import React, { useState } from 'react'
import { Grid } from '@material-ui/core'
import { t } from 'ttag'
import dayjs from '../../dayjsConfig'
import { CustomType, GoalWithProgress, ProgressType, Role, UserProfileDto, CompanyId } from '../../services/api.types'
import { convertUTCToLocalDate } from '../../common/helpers/date'
import {
  isActivityIndexType,
  isAutomatedType,
  isBonusType,
  isCollectiveType,
  isCustomType,
} from '../../common/helpers/challenges'
import { PointsLogic } from '../../views/ChallengeAdmin/components/ChallengePoints/ChallengePoints'
import YumLogActivity from '../YumLogActivity/YumLogActivity'
import YumGoalRankingJoinButtonWrapper from '../YumGoalRanking/YumGoalRankingHeader/YumGoalRankingJoinButtonWrapper'
import makeStyles from '@material-ui/core/styles/makeStyles'
import { primaryBlue } from '../../styles/material-ui-theme-v2'

/**
 * Props for YumManualEntryButtonAndDetails component
 */
export interface YumManualEntryButtonAndDetailsProps {
  /** The goal object - required */
  goal: GoalWithProgress
  /** Optional inline style for the container */
  style?: React.CSSProperties
  /** Whether token is present (leaderboard view) - optional */
  token?: string
  /** User profile data - optional, needed for dateOfBirth check */
  userDto?: UserProfileDto
  /** Function to check user role in company - optional, needed for join button */
  hasRoleInCompany?: (companyId: CompanyId, role: Role) => boolean
  /** Whether this is the "me" view - optional, defaults to false */
  isMeView?: boolean
  /** Whether manual entry is in progress - optional, defaults to false */
  isManualEntryInProgress?: boolean
  /** Whether this is legacy ranking - optional, defaults to false */
  isLegacyRanking?: boolean
  /** Current showDetails state (controlled) - optional */
  showDetails?: boolean
  /** Callback when showDetails state changes - optional */
  onShowDetailsChange?: (showDetails: boolean) => void
  /** Compact mode - hides the "Show details" / "Hide details" link - optional, defaults to false */
  compact?: boolean
}

const useStyles = makeStyles((theme) => ({
  link: {
    userSelect: 'none',
    color: primaryBlue,
    fontSize: '0.8125rem',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
      '@media (hover: none)': {
        textDecoration: 'none',
      },
    },
    '&:focus': {
      textDecoration: 'underline',
      '@media (hover: none)': {
        textDecoration: 'none',
      },
    },
    '&:active': {
      textDecoration: 'underline',
      '@media (hover: none)': {
        textDecoration: 'underline !important',
      },
    },
  },
}))

/**
 * Component that displays manual entry button, join button, and show/hide details link.
 * This component handles all business logic internally and only requires the goal prop.
 *
 * Supports both controlled and uncontrolled modes for showDetails:
 * - Controlled: Pass showDetails and onShowDetailsChange props
 * - Uncontrolled: Component manages its own state internally
 */
export default function YumManualEntryButtonAndDetails(props: YumManualEntryButtonAndDetailsProps) {
  const {
    goal,
    style,
    token,
    userDto,
    hasRoleInCompany,
    isMeView = false,
    isManualEntryInProgress = false,
    isLegacyRanking = false,
    showDetails: controlledShowDetails,
    onShowDetailsChange,
    compact = false,
  } = props

  const classes = useStyles()

  // Extract goal properties
  const { progressType, customType, manualEntryEnabled, participating, companyId, startDate, endDate } = goal

  // Calculate challenge dates and status
  const challengeStartDate = dayjs(convertUTCToLocalDate(new Date(startDate)))
  const challengeEndDate = dayjs(convertUTCToLocalDate(new Date(endDate)))
  const challengeHasStarted = challengeStartDate.isBefore(new Date())
  const challengeHasEnded = challengeEndDate.isBefore(new Date())

  // Calculate grace period
  const gracePeriodEnd = challengeEndDate.clone().add(3, 'days')
  const gracePeriodEnded = dayjs().isAfter(gracePeriodEnd)
  const isActive = challengeHasStarted && !gracePeriodEnded

  // Calculate goal type flags
  const isCustomCustom = customType === CustomType.CUSTOM
  const isAutomated = isAutomatedType(customType)
  const isTotalPoints = customType === CustomType.TOTAL_POINTS
  const isPoints = customType === CustomType.POINTS
  const isAnyActivityIndex = [ProgressType.ALL, ProgressType.PERSISTENCE_ALL].includes(progressType)

  // Parse points logic if this is a points goal
  const pointsLogic = isPoints ? (goal.pointsLogic ? JSON.parse(goal.pointsLogic) : null) : null

  // Check if goal has custom point activities (non-automated, non-bonus)
  const hasCustomPointActivities =
    pointsLogic &&
    pointsLogic.some(
      (a: PointsLogic) => (a.unit === CustomType.CUSTOM || !isAutomatedType(a.unit)) && !isBonusType(a.category),
    )

  // Calculate whether to show manual entry button
  let showManualEntryButton =
    // Don't show in iframe leaderboard
    !token &&
    // Don't show for total points challenge
    !isTotalPoints &&
    //((manualEntryEnabled && (isAutomated || !isCustom)) ||
    // Show if: manual entry enabled
    (manualEntryEnabled ||
      // ...or has custom point activities ("manual entry enabled" not relevant)
      hasCustomPointActivities ||
      // ...or is not automated
      !isAutomated ||
      // ...or is custom
      isCustomCustom)
  //!token && (((isAutomated || !isCustom) && manualEntryEnabled) || hasCustomPointActivities) && !isTotalPoints

  // For activity index challenges, only show manual entry button if manualEntryEnabled is true
  if (showManualEntryButton && isAnyActivityIndex && !manualEntryEnabled) {
    showManualEntryButton = false
  }

  // If it has only hidden "bonus" activities, hide manual entry
  if (showManualEntryButton && isPoints) {
    // Check if the goal has only bonus activities and manual entry is disabled
    // When manual entry is disabled, bonus activities are hidden from regular users
    const hasOnlyHiddenBonusActivities =
      pointsLogic && pointsLogic.length > 0 && pointsLogic.every((a: PointsLogic) => isBonusType(a.category))
    if (hasOnlyHiddenBonusActivities) {
      showManualEntryButton = false
    }
  }

  // Calculate whether user cannot perform manual entry (needs dateOfBirth for activity index)
  const userCantManualEntry = isAnyActivityIndex && (!userDto || !userDto.dateOfBirth)

  // Calculate autoClose behavior
  // Don't close if activity index or points. Except if it has only one POINTS activity...
  const hasOnlyOnePointsLogicActivity =
    pointsLogic &&
    (manualEntryEnabled
      ? pointsLogic.length === 1
      : pointsLogic.filter((a: PointsLogic) => a.unit === CustomType.CUSTOM || !isAutomatedType(a.unit)).length === 1)
  const autoClose =
    !isActivityIndexType(progressType) && (customType !== CustomType.POINTS || hasOnlyOnePointsLogicActivity)

  // Calculate whether to show join button
  // Show join button if: no token, not participating, user has USER role, challenge hasn't ended
  const isUser = hasRoleInCompany ? hasRoleInCompany(companyId, Role.USER) : false
  const showJoinButton = !participating && !token && isUser && !challengeHasEnded

  // Internal state for uncontrolled mode
  const [internalShowDetails, setInternalShowDetails] = useState(false)

  // Use controlled state if provided, otherwise use internal state
  const showDetails = controlledShowDetails !== undefined ? controlledShowDetails : internalShowDetails
  const isControlled = controlledShowDetails !== undefined

  // Toggle show details state
  const toggleShowDetails = () => {
    const newShowDetails = !showDetails
    if (isControlled) {
      // Controlled mode: notify parent
      if (onShowDetailsChange) {
        onShowDetailsChange(newShowDetails)
      }
    } else {
      // Uncontrolled mode: update internal state
      setInternalShowDetails(newShowDetails)
      if (onShowDetailsChange) {
        onShowDetailsChange(newShowDetails)
      }
    }
  }

  // Don't render if token is present (leaderboard view) or if neither button should show and challenge hasn't started
  if (token || (!showManualEntryButton && !challengeHasStarted)) {
    return null
  }

  if (compact) {
    //return showManualEntryButton && !showJoinButton ? (
    //return !showJoinButton ? (
    return (
      <YumLogActivity
        goal={goal}
        //disabled={!isActive || userCantManualEntry}
        disabled={false}
        autoClose={autoClose}
        isMeView={isMeView}
        isInProgress={isManualEntryInProgress}
        isBonus={false}
        compact={true}
        showManualEntryButton={showManualEntryButton}
        style={style}
        challengeHasStarted={challengeHasStarted}
      />
    )
    //) : null
  }

  // Merge base layout style with optional external style overrides.
  const containerStyle: React.CSSProperties = {
    ...(isLegacyRanking ? { marginBottom: '16px', flexDirection: 'row-reverse' as const } : { marginBottom: '16px' }),
    ...(style || {}),
  }

  return (
    <Grid
      container
      justifyContent={showManualEntryButton || showJoinButton ? 'space-between' : 'center'}
      wrap="nowrap"
      alignItems="center"
      style={containerStyle}
    >
      {/* Show/Hide details link - only shown when challenge has started and not in compact mode */}
      {challengeHasStarted && (
        <Grid item>
          <div className={classes.link} onClick={toggleShowDetails}>
            {!showDetails ? t`Show details` : t`Hide details`}
          </div>
        </Grid>
      )}

      {/* Manual entry button or join button */}
      {showManualEntryButton || showJoinButton ? (
        <Grid item style={!challengeHasStarted ? { marginLeft: 'auto' } : {}}>
          {/* Manual entry button - shown when showManualEntryButton is true and showJoinButton is false */}
          {showManualEntryButton && !showJoinButton ? (
            <YumLogActivity
              goal={goal}
              disabled={!isActive || userCantManualEntry}
              autoClose={autoClose}
              isMeView={isMeView}
              isInProgress={isManualEntryInProgress}
              isBonus={false}
              showManualEntryButton={true}
            />
          ) : null}

          {/* Join button - shown when showJoinButton is true */}
          {showJoinButton ? <YumGoalRankingJoinButtonWrapper goal={goal} isLegacyRanking={false} /> : null}
        </Grid>
      ) : null}
    </Grid>
  )
}
